import { CommonModule } from "@angular/common";
import { Component, Inject, inject, OnInit } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { TranslateModule } from "@ngx-translate/core";

import { SharedModule } from "../../shared.module";
import { FormContainerComponent } from "../ui-components/forms/form-container/form-container.component";
import { FormInputComponent } from "../ui-components/forms/form-input/form-input.component";
import { DropdownComponent } from "../ui-components/dropdown/dropdown.component";
import { DropdownOption } from "../ui-components/dropdown/dropdown.model";
import { FormInputType } from "../ui-components/forms/form-input/form-input.model";
import { VoucherTypeEnum } from "src/app/enums/voucher-type.enum";

export interface VoucherReturnValueDialogModel {
  maxGift: number;
  maxBenefit: number;
  configMaxGift: number | null;
  configMaxBenefit: number | null;
  currency: string;
  selectedVoucherType: VoucherTypeEnum;
}

export interface VoucherReturnValueDialogResult {
  voucherType: VoucherTypeEnum;
  amount: number;
}

@Component({
  selector: "app-voucher-return-value-dialog",
  templateUrl: "./voucher-return-value-dialog.component.html",
  styleUrls: ["./voucher-return-value-dialog.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    SharedModule,
    FormContainerComponent,
    FormInputComponent,
    DropdownComponent,
  ],
})
export class VoucherReturnValueDialogComponent implements OnInit {
  public FormInputType = FormInputType;
  public VoucherTypeEnum = VoucherTypeEnum;

  public typeOptions: DropdownOption[] = [];
  public maxForSelectedType = 0;

  public form: FormGroup;

  private _dialogRef = inject(
    MatDialogRef<VoucherReturnValueDialogComponent, VoucherReturnValueDialogResult | null>
  );

  constructor(@Inject(MAT_DIALOG_DATA) public data: VoucherReturnValueDialogModel) {
    // Initialize with disabled voucherType control to avoid ExpressionChangedAfterChecked
    this.form = new FormGroup({
      voucherType: new FormControl<VoucherTypeEnum | null>(
        { value: data.selectedVoucherType, disabled: true },
        [Validators.required],
      ),
      amount: new FormControl<number | null>(null, [Validators.required, Validators.min(0.01)]),
    });
  }

  ngOnInit(): void {
    const giftMax = this.computeMax(this.data.maxGift, this.data.configMaxGift);
    const benefitMax = this.computeMax(this.data.maxBenefit, this.data.configMaxBenefit);

    if (giftMax > 0) {
      this.typeOptions.push({ value: VoucherTypeEnum.Normal, label: "webPos.Voucher.GiftCertificate" });
    }
    if (benefitMax > 0) {
      this.typeOptions.push({ value: VoucherTypeEnum.Benefit, label: "webPos.Voucher.BenefitCertificate" });
    }

    // Keep the main-form selection when it is available. If it is no longer
    // refundable, select the only available voucher type automatically.
    const selectedTypeIsAvailable = this.typeOptions.some(option => option.value === this.data.selectedVoucherType);
    const preselected = selectedTypeIsAvailable
      ? this.data.selectedVoucherType
      : this.typeOptions.length === 1
        ? this.typeOptions[0].value
        : null;
    this.form.controls.voucherType.setValue(preselected, { emitEvent: false });

    this.maxForSelectedType =
      preselected === VoucherTypeEnum.Benefit ? benefitMax : giftMax;
    this.form.controls.amount.setValidators([
      Validators.required,
      Validators.min(0.01),
      Validators.max(this.maxForSelectedType),
    ]);
    this.form.controls.amount.setValue(this.maxForSelectedType > 0 ? this.maxForSelectedType : null);
    this.form.controls.amount.updateValueAndValidity();
  }

  private computeMax(available: number, configMax: number | null): number {
    const a = Math.max(0, available || 0);
    if (configMax == null || configMax <= 0) {
      return a;
    }
    return Math.min(a, configMax);
  }

  public onConfirm(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.getRawValue();
    this._dialogRef.close({
      voucherType: value.voucherType,
      amount: Number(value.amount),
    });
  }

  public onCancel(): void {
    this._dialogRef.close(null);
  }
}
